import React, { useCallback, useEffect } from 'react';
import { useActiveFolderData } from '../../hooks/useActiveFolderData';
import { useFileExplorerContext } from '../../hooks/useFileExplorerContext';
import { LoadingSpinner } from '../common';
import { useFileExplorerDragContext } from './Context/FileExplorerDndContext';
import { FileExplorerDropzone } from './FileExplorerDropzone';
import {
  FileExplorerGridFileItem,
  FileExplorerGridFolderItem,
} from './FileExplorerGridItem';
import {
  FileGridItemActions,
  FolderGridItemActions,
} from './FileExplorerGridItemActions';
import { DraggedItem } from './hooks/useFileDragAndDrop';
import { TreeFolder } from './services';

export function FileExplorerGridContent() {
  const {
    activeFolder, newlyAddedItems, setActiveFolder, expandFolders,
  } = useFileExplorerContext();

  const { data, isLoading } = useActiveFolderData();
  const folders = data?.data?.folders || [];
  const files = data?.data?.files || [];
  const {
    selectedKeys, handleSelection, getSelectedItems, clearSelection,
  } = useFileExplorerDragContext();

  useEffect(() => {
    // We need to detect clicks outside of the React tree
    // (like the whitespace that is added via HTML template),
    // In order to clear the selection
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-slot="file-explorer-grid-item"]')) {
        clearSelection();
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [clearSelection]);

  // Convert the current grid selection to DraggedItem[] so every draggable
  // carries the full selection in its drag-data.  The actual set of items that
  // will be moved is decided in onDragEnd
  const selectedDraggedItems: DraggedItem[] = getSelectedItems().map((item) => {
    const isFile = 'category' in item;
    return {
      id: item.id,
      type: (isFile ? 'file' : 'folder') as DraggedItem['type'],
      name: isFile ? item.title : item.name,
    };
  });

  const handleFolderDoubleClick = useCallback(
    (folder: TreeFolder) => {
      if (!activeFolder) return;

      const newPath = [
        ...activeFolder.path,
        { id: folder.id, name: folder.name },
      ];

      const ancestorIds = newPath.map((node) => node.id);
      expandFolders(ancestorIds);

      setActiveFolder({
        id: folder.id,
        path: newPath,
        metadata: {
          id: folder.id,
          name: folder.name,
          permissions: folder.permissions,
          shareable_link: folder.shareable_link,
        },
      });
    },
    [activeFolder, setActiveFolder, expandFolders],
  );

  if (isLoading) {
    return (
      <div className="file-explorer-grid p-4 z-1 d-flex align-items-center justify-content-center h-100">
        <LoadingSpinner size={48} borderWidth={4} color="info" />
      </div>
    );
  }

  if (!data?.data || !activeFolder) return null;

  return (
    <div className="file-explorer-grid p-4 z-1">
      {folders.map((folder, index) => (
        <FileExplorerGridFolderItem
          key={folder.id}
          onDoubleClick={(e) => {
            handleFolderDoubleClick(folder);
            e.stopPropagation();
          }}
          onClick={(e) => handleSelection(folder, index, e)}
          actions={<FolderGridItemActions node={folder} />}
          data-newly-added={newlyAddedItems.has(folder.id)}
          data-is-selected={selectedKeys.has(`folder-${folder.id}`)}
          node={{ ...folder, index }}
          selectedItems={selectedDraggedItems}
        />
      ))}
      {files.map((file, index) => (
        <FileExplorerGridFileItem
          key={file.id}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            handleSelection(file, folders.length + index, e);
          }}
          data-is-selected={selectedKeys.has(`file-${file.id}`)}
          data-newly-added={newlyAddedItems.has(file.id)}
          actions={(
            <FileGridItemActions
              node={file}
              permissions={activeFolder.metadata.permissions}
              shareableLink={activeFolder.metadata.shareable_link}
            />
          )}
          node={{ ...file, index: folders.length + index }}
          selectedItems={selectedDraggedItems}
        />
      ))}
    </div>
  );
}

function FileExplorerGridEmptyState() {
  return (
    <div className="h-100 w-100 d-flex flex-column align-items-center justify-content-center gap-2">
      <img src="/static/img/icons/folder.svg" alt="Folder" height={64} />
      <span className="fs-6 fw-semibold">Selecciona una carpeta</span>
      <p className="w-50 text-center text-muted">
        Selecciona una carpeta para interactuar con su contenido.
      </p>
    </div>
  );
}

export function FileExplorerGrid() {
  const { activeFolder } = useFileExplorerContext();

  return (
    <FileExplorerDropzone>
      <div className="file-explorer-grid-container">
        {activeFolder ? (
          <FileExplorerGridContent />
        ) : (
          <FileExplorerGridEmptyState />
        )}
      </div>
    </FileExplorerDropzone>
  );
}
